import * as React from 'react';

export type ProgressBarProps = {
  value: number;
  variant?: 'flat' | 'segmented';
  segments?: number;
  className?: string;
};

function normalizeValue(value: number): number {
  if (value > 1) return Math.min(1, Math.max(0, value / 100));
  return Math.min(1, Math.max(0, value));
}

function join(...classes: Array<string | false | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ value, variant = 'flat', segments = 5, className }, ref) => {
    const ratio = normalizeValue(value);

    if (variant === 'segmented') {
      const completed = Math.round(ratio * segments);
      return (
        <div
          ref={ref}
          role="progressbar"
          aria-valuenow={Math.round(ratio * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          className={join('flex w-full items-center gap-2', className)}
          data-test-id="progress-bar-segmented"
        >
          {Array.from({ length: segments }, (_, i) => (
            <div
              key={i}
              className={join(
                'h-1.5 min-w-px flex-1 rounded-full',
                i < completed ? 'bg-white' : 'bg-white/15'
              )}
            />
          ))}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        className={join('h-1.5 w-full overflow-hidden rounded-full bg-white/15', className)}
        data-test-id="progress-bar-flat"
      >
        <div
          className="h-full rounded-full bg-white transition-[width] duration-300"
          style={{ width: `${ratio * 100}%` }}
        />
      </div>
    );
  }
);
ProgressBar.displayName = 'ProgressBar';

export { ProgressBar };
